/**
 * Turning gestures into a shot direction (UI-001).
 *
 * A `ShotInput` carries a unit vector rather than an angle, because converting an
 * angle needs `sin`/`cos` and those are not correctly rounded across engines — the
 * server must be able to replay the shot bit-for-bit (ARCHITECTURE.md §5.5).
 *
 * Trig here is fine, and unavoidable: fine adjust IS a rotation. The rule is about
 * what crosses the boundary, not about what the UI does to arrive at it. Every
 * direction leaves this file normalised and rounded to 1e-6, so the value the server
 * replays is exactly the value the player shot.
 */

/**
 * One press of fine adjust: a tenth of a degree (owner, 2026-09-27, UI-014; it was a
 * quarter, which the one-decimal readout showed as uneven jumps of .2 and .3). At 1.8 m,
 * about 3 mm. Holding the button repeats, faster the longer it is held (hold-repeat.ts).
 */
export const FINE_ADJUST_DEGREES = 0.1;
export const FINE_ADJUST_RADIANS = FINE_ADJUST_DEGREES * (Math.PI / 180);

export interface Direction {
  readonly dx: number;
  readonly dy: number;
}

/**
 * Snap a component to the 1e-6 grid the shot input is defined on.
 *
 * Rounding both components moves length² by at most ~2e-6, comfortably inside the
 * 1e-5 tolerance `validateShot` allows.
 */
export function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

/** Normalise and canonicalise. `undefined` for a zero-length or non-finite vector. */
export function toDirection(dx: number, dy: number): Direction | undefined {
  const length = Math.sqrt(dx * dx + dy * dy);
  if (!Number.isFinite(length) || length < 1e-9) return undefined;
  return { dx: round6(dx / length), dy: round6(dy / length) };
}

/** Aim from one point at another — the drag gesture, in table coordinates. */
export function directionTo(
  fromXM: number,
  fromYM: number,
  toXM: number,
  toYM: number,
): Direction | undefined {
  return toDirection(toXM - fromXM, toYM - fromYM);
}

/** Rotate a direction. Positive is counter-clockwise in table coordinates. */
export function rotate(direction: Direction, radians: number): Direction {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const rotated = toDirection(
    direction.dx * cos - direction.dy * sin,
    direction.dx * sin + direction.dy * cos,
  );
  // A unit vector cannot rotate to zero length, so this only guards a corrupt input.
  return rotated ?? direction;
}

/**
 * Nudge the aim one step, in the sense the PLAYER sees.
 *
 * ⚠️ `screenTurnSign` is not optional bookkeeping. The table is drawn rotated on a
 * portrait phone and the natural way round on a landscape window (UI-003), and the
 * rotated mapping FLIPS handedness: a counter-clockwise turn in table coordinates
 * reads as clockwise on the cloth. A fine-adjust button that got this wrong would
 * move the aim the opposite way to the arrow on it — the single most confusing bug a
 * pool game can have, and it would be wrong in exactly one of the two orientations.
 *
 * So the sign comes from `viewport.screenTurnSign`, which measures it from the live
 * mapping rather than declaring it. Pass it in; do not guess it.
 */
export function nudge(
  direction: Direction,
  towards: 'left' | 'right',
  screenTurnSign: number,
): Direction {
  const sense = towards === 'left' ? 1 : -1;
  return rotate(direction, sense * screenTurnSign * FINE_ADJUST_RADIANS);
}
